import React from 'react';
import { Link } from 'react-router-dom';
import './actionLinkButton.scss';

type Theme = 'light' | 'dark';
type IconPosition = 'start' | 'end';

export interface ActionLinkButtonProps {
    /** Светлая или тёмная тема (влияет на стили и подбор иконки) */
    theme?: Theme;

    /** Текст. Если не передан - компонент квадратный, иконка по центру */
    text?: React.ReactNode;

    /** Путь к иконке для светлой темы (или универсальной) */
    iconSrc?: string;

    /** Путь к иконке для тёмной темы (если не задано - возьмётся iconSrc) */
    iconSrcDark?: string;

    /** Alt для иконки (для a11y) */
    iconAlt?: string;

    /** Позиция иконки, если есть текст */
    iconPosition?: IconPosition;

    /** Бейдж-счётчик (например, кол-во товаров в корзине) */
    count?: number;

    /** Доп. классы-миксы */
    className?: string;

    /** aria-label (если текста нет) */
    ariaLabel?: string;

    /** Состояние disabled (только для кнопки или внешний <a> без навигации) */
    disabled?: boolean;

    /** ВАРИАНТ 1: действие - функция */
    onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;

    /** ВАРИАНТ 2: внутренняя ссылка (react-router Link) */
    to?: string;

    /** ВАРИАНТ 3: внешняя ссылка */
    href?: string;
    target?: '_blank' | '_self' | '_parent' | '_top';
    rel?: string;

    /** data-* и т.п. */
    [key: string]: any;
}

export const ActionLinkButton: React.FC<ActionLinkButtonProps> = ({
    theme = 'light',
    text,
    iconSrc,
    iconSrcDark,
    iconAlt = '',
    iconPosition = 'start',
    count,
    className = '',
    ariaLabel,
    disabled = false,
    onClick,
    to,
    href,
    target,
    rel,
    ...rest
}) => {
    const isRouterLink = typeof to === 'string' && to.length > 0;
    const isExternalLink = typeof href === 'string' && href.length > 0;
    const isButton = !!onClick && !isRouterLink && !isExternalLink;

    // Рантайм-предостережения: не ломаем рендер, но предупреждаем в dev
    if (process.env.NODE_ENV !== 'production') {
        const provided = [isButton, isRouterLink, isExternalLink].filter(Boolean).length;
        if (provided === 0) {
            console.warn('[ActionLinkButton]: ожидается один из вариантов: onClick | to | href');
        }
        if (provided > 1) {
            console.warn('[ActionLinkButton]: задано сразу несколько вариантов. Используйте ровно один: onClick ИЛИ to ИЛИ href');
        }
    }

    const hasText = React.Children.count(text) > 0;
    const chosenIcon = theme === 'dark' && iconSrcDark ? iconSrcDark : iconSrc;
    const hasIcon = !!chosenIcon;

    const iconOnly = hasIcon && !hasText;

    const baseClass = 'uiAction';
    const classes = [
        baseClass,
        `${baseClass}_theme_${theme}`,
        iconOnly ? `${baseClass}_iconOnly` : '',
        hasText && iconPosition === 'end' ? `${baseClass}_iconEnd` : '',
        disabled ? `${baseClass}_disabled` : '',
        typeof count === 'number' && count > 0 ? `${baseClass}_hasCount` : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const commonContent = (
        <>
            {hasIcon && <img className={`${baseClass}__icon`} src={chosenIcon} alt={iconAlt} aria-hidden={iconAlt ? undefined : true} />}

            {hasText && <span className={`${baseClass}__text`}>{text}</span>}

            {typeof count === 'number' && count > 0 && (
                <span className={`${baseClass}__count`} aria-label={`Количество: ${count}`}>
                    {count}
                </span>
            )}
        </>
    );

    // Безопасные атрибуты для внешних ссылок
    const safeRel = target === '_blank' ? (rel && rel.length > 0 ? rel : 'noopener noreferrer') : rel;

    // Ветвление по типу элемента
    if (isRouterLink) {
        return (
            <Link to={to as string} className={classes} aria-label={!hasText ? ariaLabel : undefined} {...rest}>
                {commonContent}
            </Link>
        );
    }

    if (isExternalLink) {
        return (
            <a
                href={href as string}
                target={target}
                rel={safeRel}
                className={classes}
                onClick={onClick as any}
                aria-label={!hasText ? ariaLabel : undefined}
                aria-disabled={disabled ? true : undefined}
                {...rest}
            >
                {commonContent}
            </a>
        );
    }

    // По умолчанию - кнопка (если есть onClick; иначе просто неактивная кнопка)
    return (
        <button type="button" className={classes} onClick={onClick as any} disabled={disabled} aria-label={!hasText ? ariaLabel : undefined} {...rest}>
            {commonContent}
        </button>
    );
};

export default ActionLinkButton;
